import {useSelector} from "react-redux";

import {
    type AppreciationKey,
    getAppreciationId,
    selectAppreciationHasValueCurried,
    selectAppreciationIsVisibleCurried,
} from "../../redux/appreciationsUtil";
import {selectReadOnly} from "../../redux/persistenceSlice";
import AppreciationToolbarButton from "./AppreciationToolbarButton";
import {resolveAppreciationId, useAppreciationVisible} from "./appreciationHooks";

interface AppreciationToolbarProps {
    /** option1: provide appreciation key */
    appreciationKey?: AppreciationKey;
    /** option2: provide appreciation page and tab, to calculate key */
    page?: string;
    tab?: string;
    appreciationVisible: boolean;
    setAppreciationVisible: (visible: boolean) => void;
    /** passed through to AppreciationToolbarButton -- see there */
    linkTo?: string;
}

/**
 * React component for a toolbar, which only contains an appreciation button, and is invisible when the appreciation button is not rendered.
 */
export default function AppreciationToolbar({
    appreciationKey,
    page,
    tab,
    appreciationVisible,
    setAppreciationVisible,
    linkTo,
}: AppreciationToolbarProps) {
    const effectiveAppreciationKey = resolveAppreciationId(appreciationKey, page, tab);

    const buttonVisible = useSelector(selectAppreciationIsVisibleCurried(effectiveAppreciationKey));
    const hasValue = useSelector(selectAppreciationHasValueCurried(effectiveAppreciationKey));
    const readOnly = useSelector(selectReadOnly);
    const appreciationId = getAppreciationId(effectiveAppreciationKey);
    const appreciationAllowed = useAppreciationVisible(effectiveAppreciationKey);

    if (
        // Appreciation UI requires permission, except for the always-visible exception fields (#18607)
        !appreciationAllowed ||
        (page && !tab) ||
        (!buttonVisible && !readOnly) ||
        (readOnly && !hasValue)
    ) {
        return null;
    }

    return (
        <div id={`appreciationToolbar_${appreciationId}`} className="HeaderToolbarRight">
            <AppreciationToolbarButton
                appreciationKey={appreciationKey}
                page={page}
                tab={tab}
                appreciationVisible={appreciationVisible}
                setAppreciationVisible={setAppreciationVisible}
                linkTo={linkTo}
            />
        </div>
    );
}
